/* eslint-disable yoda */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, filter, maxBy } from 'lodash';
import Stars from '../elements-stars/stars';
import EllipsisDropdown from '../elements-dropdown/EllipsisDropdown';
import DeactivatedListingsPanel from './DeactivatedListingsPanel';
import ActiveListingsPanel from './ActiveListingsPanel';
import MyListingsSpaceBanner from './MyListingsSpaceBanner';
import spaceBannerStatus from './spaceBannerStatus';
import { isFsvListing } from './utils/listing-fsv.utils';
import ListingImage from '../elements-image/ListingImageV2';
import { FormattedMessage } from 'react-intl';
import { StatsigContext } from '../elements-statsig-provider/statsig-context';

const LiveSpacePanel = ({
  space,
  listings,
  showTooltip,
  updateNux,
  showAddonsTooltip,
  workflowBySpace,
  allowVendorEdits,
  ...props
}) => {
  const {
    featureGates
  } = useContext(StatsigContext);

  const isEnabled = featureGates.pause_listing ?? false;

  const {
    _id: spaceId = '',
    title = '',
    space_review_stars = 0,
    space_review_count = 0,
    parking_info: parkingInfo,
    cleaning_info: cleaningInfo
  } = space;
  const mostRecentWorkflow = maxBy(workflowBySpace, (workflow) => new Date(workflow.date_modified).getTime());
  const workflowState = get(mostRecentWorkflow, 'obj_state', '');
  const hostUpdateUrl = get(mostRecentWorkflow, 'host_update_url', '');
  let listingCount = 0;

  const images = listings.reduce((acc, listing = {}) => {
    acc[listing.use_type] = get(listing, 'images[0].external_id');
    return acc;
  }, {});
  const spaceImageExternalId = images.Production || images['Off-Site'] || images.Event;

  return (
    <div className={classnames('mylistings-space space-panel', { '!tw-mb-40': allowVendorEdits })}>
      <div className="flex middle space-between column-xs mylistings-space-header">

        <div className="cover-photo">
          <ListingImage
            sizes="(min-width: 992px) 200px, (min-width: 768px) 320px, 100vw"
            imgId={spaceImageExternalId}
            classname="rounded-image"
            ratio={1.5}
          />
        </div>

        <div className="tw-basis-full tw-self-stretch md:tw-basis-4/5 md:tw-self-auto">
          <div className="listing-title heavy">
            {title}
          </div>
          <div>
            {
              0 !== space_review_count &&
                <Stars
                  reviewStars={space_review_stars}
                  reviewCount={space_review_count}
                  starClassName="yellow-star"
                />
            }
          </div>

          <div className="flex middle space-between flex-wrap">
            <div className="space-description text-gray-600"><FormattedMessage id="hosting.live_space_panel.space_description_text" defaultMessage="Address, Parking, Arrival instructions, Wi-Fi, Cancellation policy, etc."/></div>
            <div className="flex tw-w-full md:tw-w-auto">
              <div className="margin-md-right tw-flex-1 md:tw-flex-none">
                <a
                  className="btn btn-block btn-default btn-outlined-light"
                  href={`/your-space/${spaceId}`}
                  data-testing-id={`edit-space-${spaceId}`}
                >
                  <FormattedMessage id="hosting.live_space_panel.edit_space_link_text" defaultMessage="Edit Space"/>
                </a>
              </div>
              <div>
                <EllipsisDropdown
                  buttonClassName="btn-default btn-outlined-light text-large"
                  listItems={
                    [
                      ...(0 < filter(listings, { status: 'ACTIVATED' }).length ?
                        [{
                          title: <FormattedMessage id="hosting.live_space_panel.dropdown_title_manage_add_ons" defaultMessage="Manage add-ons"/>,
                          href: `/hosting/add-ons/${spaceId}`
                        }] : []),
                      {
                        title: (<FormattedMessage id="hosting.live_space_panel.dropdown_title_delete" defaultMessage="Delete"/>),
                        className: 'text-red',
                        onClick: () => props.toggleModal('DeleteSpaceModal', { spaceId })
                      }
                    ]
                  }
                />
              </div>

            </div>
          </div>
        </div>

      </div>
      {
        workflowState &&
        <MyListingsSpaceBanner
          bannerObject={spaceBannerStatus({ status: workflowState, link: hostUpdateUrl, spaceId })}
        />
      }
      {
        (!workflowState && !cleaningInfo) ?
          <MyListingsSpaceBanner
            bannerObject={spaceBannerStatus({
              status: 'ADD_CLEANING',
              link: `/your-space/${spaceId}/cleaning-policy`,
            })}
          /> :
          (!workflowState && !parkingInfo) ?
            <MyListingsSpaceBanner
              bannerObject={spaceBannerStatus({
                status: 'PARKING_REQUIRED',
                link: `/your-space/${spaceId}/parking`,
              })}
            /> : null
      }
      <div className="mylistings-table-body">
        <div className="heavy text-large"><FormattedMessage id="hosting.live_space_panel.customize_text" defaultMessage="Customize for different activities"/></div>
        {
          ['Off-Site', 'Production', 'Event'].map((useType) => {
            const filteredListing = filter(listings, listing => {
              return get(listing, 'use_type', '') === useType;
            });
            const selectedListing =
              filteredListing.find((listing) => !['DRAFT', 'DEACTIVATED'].includes(listing?.status)) ||
              filteredListing.find((listing) => listing?.status === 'DRAFT') ||
              filteredListing.find((listing) => listing?.status === 'DEACTIVATED') ||
              filteredListing[0];
            const listingId = get(selectedListing, '_id', '');
            const workflow = maxBy(filter(workflowBySpace, { listing_id: listingId }), (item) => new Date(item.date_modified).getTime());

            if (filteredListing.length) {
              listingCount++;
              if ((selectedListing?.status === 'DEACTIVATED') && isEnabled) {
                return (
                  <DeactivatedListingsPanel
                    key={`${spaceId}-${useType}`}
                    useType={useType}
                    spaceId={spaceId}
                    isDeactivated={true}
                    isFsv={isFsvListing(selectedListing)}
                  />
                );
              }
              return (
                <ActiveListingsPanel
                  key={`${spaceId}-${useType}`}
                  {...selectedListing}
                  toggleModal={props.toggleModal}
                  showTooltip={showTooltip && 1 === listingCount}
                  updateNux={updateNux}
                  workflow={workflow}
                />
              );
            }
            return (
              <DeactivatedListingsPanel
                key={`${spaceId}-${useType}`}
                useType={useType}
                spaceId={spaceId}
              />
            );
          })
        }
      </div>
    </div>
  );
};

LiveSpacePanel.propTypes = {
  space: PropTypes.object.isRequired,
  listings: PropTypes.array,
  toggleModal: PropTypes.func.isRequired,
  showTooltip: PropTypes.bool,
  updateNux: PropTypes.func,
  showAddonsTooltip: PropTypes.bool,
  workflowBySpace: PropTypes.array,
  allowVendorEdits: PropTypes.bool
};

export default LiveSpacePanel;
